import { useState, useEffect } from 'react';
import { Plus, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import { GOLD, fmt, Stat, Card, Row } from './shared';

const InvoiceModal = ({ api, jobId, onClose, onCreated }) => {
  const [type, setType] = useState('progress');
  const [items, setItems] = useState([{ description: '', amount: 0 }]);
  const [notes, setNotes] = useState('');
  const total = items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const create = async () => {
    try {
      await api.post('/accounting/invoices', { job_id: jobId, invoice_type: type, line_items: items.map(i => ({ description: i.description, amount: parseFloat(i.amount) || 0 })), notes });
      toast.success('Invoice created'); onCreated();
    } catch { toast.error('Failed'); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="invoice-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><FileText className="w-5 h-5" /> New Invoice</h3>
          <button onClick={onClose} data-testid="inv-modal-close"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <label className="text-xs font-medium text-gray-500">Type</label>
        <select data-testid="inv-type" value={type} onChange={(e) => setType(e.target.value)} className="w-full mt-1 mb-4 px-3 py-2 border border-gray-300 rounded-lg text-sm">
          {['deposit', 'progress', 'milestone', 'final', 'time_material'].map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
        {items.map((it, idx) => (
          <div key={idx} className="flex gap-2 mb-2">
            <input data-testid={`inv-item-desc-${idx}`} placeholder="Description" value={it.description} onChange={(e) => { const n = [...items]; n[idx].description = e.target.value; setItems(n); }} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            <input data-testid={`inv-item-amt-${idx}`} type="number" placeholder="Amount" value={it.amount} onChange={(e) => { const n = [...items]; n[idx].amount = e.target.value; setItems(n); }} className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
        ))}
        <button data-testid="inv-add-line" onClick={() => setItems([...items, { description: '', amount: 0 }])} className="text-xs text-[#C6A87C] font-medium mb-4">+ Add Line</button>
        <div className="flex items-center justify-between py-3 border-t border-gray-200 mb-4">
          <span className="font-semibold">Total</span><span className="font-bold text-lg" style={{ color: GOLD }}>{fmt(total)}</span>
        </div>
        <button data-testid="inv-create-btn" onClick={create} className="w-full py-2 bg-[#C6A87C] text-white font-medium rounded-lg hover:opacity-90">Create Invoice</button>
      </div>
    </div>
  );
};

export const ProfitTab = ({ api, jobId, readOnly }) => {
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [showInvForm, setShowInvForm] = useState(false);
  const reload = () => {
    if (!jobId) return;
    api.get(`/accounting/jobs/${jobId}/financial-summary`).then(r => setSummary(r.data)).catch(() => {});
    api.get(`/accounting/invoices?job_id=${jobId}`).then(r => setInvoices(r.data.invoices || [])).catch(() => {});
  };
  useEffect(reload, [api, jobId]);
  if (!jobId) return <p className="text-gray-500">Select a project.</p>;
  if (!summary) return <p className="text-gray-500">Loading...</p>;
  const p = summary.profitability;
  return (
    <div data-testid="fos-profit-tab">
      <div className="grid sm:grid-cols-4 gap-4 mb-6">
        <Stat label="Contract Amount" value={fmt(summary.revenue?.contract_amount)} testid="profit-contract" />
        <Stat label="Total Cost" value={fmt(summary.costs?.total)} accent="#dc2626" testid="profit-cost" />
        <Stat label="Gross Profit" value={fmt(p?.gross_profit)} accent="#16a34a" testid="profit-gross" />
        <Stat label="Gross Margin" value={`${p?.gross_margin || 0}%`} accent={GOLD} testid="profit-margin" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="font-semibold text-gray-900 mb-4">Cost Breakdown</h3>
          <div className="space-y-3 text-sm">
            <Row label="Labor" value={`${fmt(summary.costs?.labor?.cost)} (${summary.costs?.labor?.hours || 0} hrs)`} />
            <Row label="Materials" value={fmt(summary.costs?.materials)} />
            <Row label="Equipment" value={fmt(summary.costs?.equipment)} />
            <Row label="Change Orders" value={fmt(summary.costs?.change_orders)} />
            <div className="pt-3 border-t border-gray-200"><Row label="Total Cost" value={fmt(summary.costs?.total)} bold /></div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Invoices</h3>
            {!readOnly && (
              <button data-testid="inv-new-btn" onClick={() => setShowInvForm(true)} className="inline-flex items-center gap-1 text-sm text-[#C6A87C] font-medium hover:underline">
                <Plus className="w-4 h-4" /> New Invoice
              </button>
            )}
          </div>
          <div className="space-y-3 text-sm mb-4">
            <Row label="Invoiced" value={fmt(summary.revenue?.invoiced)} />
            <Row label="Paid" value={fmt(summary.revenue?.paid)} accent="#16a34a" />
            <Row label="Outstanding" value={fmt(summary.revenue?.outstanding)} accent="#d97706" />
          </div>
          <table className="w-full text-sm">
            <tbody data-testid="invoice-table">
              {invoices.length === 0 && <tr><td className="py-2 text-gray-400">No invoices.</td></tr>}
              {invoices.map(inv => (
                <tr key={inv.id} className="border-b border-gray-100">
                  <td className="py-2 font-mono text-xs">{inv.invoice_number}</td>
                  <td>{fmt(inv.total)}</td>
                  <td><span className={`text-xs px-2 py-0.5 rounded-full ${inv.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{inv.status}</span></td>
                  <td className="text-right">
                    {!readOnly && inv.status !== 'paid' && (
                      <button data-testid={`inv-paid-${inv.id}`} onClick={async () => { await api.post(`/accounting/invoices/${inv.id}/mark-paid`); reload(); toast.success('Marked paid'); }} className="text-xs text-[#C6A87C] hover:underline">Mark Paid</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
      {showInvForm && <InvoiceModal api={api} jobId={jobId} onClose={() => setShowInvForm(false)} onCreated={() => { setShowInvForm(false); reload(); }} />}
    </div>
  );
};
